import React, { useState, useEffect } from 'react';
import { get } from '../api';
import { Button } from '../components/Button';
import { Square2StackIcon, CubeIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useNavigate } from '@tanstack/react-router';
import { motion } from 'framer-motion';
import '@google/model-viewer';

const CURATED_EXAMPLES = [
  {
    id: 'env-asset',
    prompt: "A collection of stylized pine trees, low polygon count, gradient green textures, snowy tips, suitable for a mobile strategy game map.",
    image: "https://files.compress-glb.com/examples/the-environment-asset/Front_view.png",
    model_url: "https://files.compress-glb.com/examples/the-environment-asset/the-environment-asset.glb"
  },
  {
    id: 'fashion',
    prompt: "Futuristic high-top sneaker, chunky sole, holographic white leather material, neon blue accents, streetwear style, clean commercial render.",
    image: "https://files.compress-glb.com/examples/the-fashion-item/Front_view.png",
    model_url: "https://files.compress-glb.com/examples/the-fashion-item/the-fashion-item.glb"
  },
  {
    id: 'mobile-char',
    prompt: "A cute blocky robot helper, voxel art style, orange and white color scheme, simple geometry, floating antennas, vibrant colors.",
    image: "https://files.compress-glb.com/examples/the-mobile-game-character/Front_view.png",
    model_url: "https://files.compress-glb.com/examples/the-mobile-game-character/the-mobile-game-character.glb"
  },
  {
    id: 'scifi',
    prompt: "Worn astronaut helmet, gold reflective visor, white tactical padding, scratches and battle damage, realistic sci-fi prop, Unreal Engine 5 style.",
    image: "https://files.compress-glb.com/examples/the-sci-fi-prop/Front_view.png",
    model_url: "https://files.compress-glb.com/examples/the-sci-fi-prop/the-sci-fi-prop.glb"
  }
];

function ModelPreview({ model }) {
  const [show3D, setShow3D] = useState(false);

  return (
    <div className="group relative aspect-square rounded-2xl overflow-hidden bg-background-soft border border-border/50 transition-all hover:shadow-xl">
      {show3D ? (
        <div className="w-full h-full relative bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-gray-800 via-gray-950 to-black">
          <model-viewer
            src={model.model_url}
            poster={model.image}
            alt={model.prompt}
            auto-rotate
            camera-controls
            shadow-intensity="1"
            shadow-softness="1"
            environment-image="neutral"
            exposure="1.0"
            style={{ width: '100%', height: '100%' }}
          >
            <div slot="poster" className="flex items-center justify-center w-full h-full bg-background-soft">
              <div className="w-16 h-16 border-2 border-foreground/20 border-t-foreground rounded-full animate-spin"></div>
            </div>
          </model-viewer>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShow3D(false);
            }}
            className="absolute top-2 right-2 p-1 bg-black/50 hover:bg-black/70 text-white rounded-full backdrop-blur-sm transition-colors z-10"
            title="Close 3D view"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
      ) : (
        <>
          <img
            src={model.image}
            alt={model.prompt}
            className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-all duration-500"
          />
          <div className="absolute inset-0 flex flex-col justify-end p-4 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <p className="text-white text-[10px] font-mono uppercase tracking-wider line-clamp-2 mb-3">{model.prompt}</p>
             <button
              onClick={(e) => {
                e.stopPropagation();
                setShow3D(true);
              }}
              className="w-full py-2 bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white text-xs font-bold rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              <CubeIcon className="w-4 h-4" />
              View 3D
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default function AiStudioModelsPage() {
  const [modelGroups, setModelGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchJobs();
  }, []);

  const createNew = () => {
    navigate({ to: '/app/ai-studio/create' });
  };

  const fetchJobs = async () => {
    try {
      const response = await get('/api/1/generate/jobs?limit=50');
      setModelGroups(response.groups);
    } catch (err) {
      console.error('Failed to fetch jobs:', err);
      setError('Failed to load models.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-6 py-10 flex justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-6xl mx-auto px-6 py-10 text-center text-danger-foreground">
        {error}
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-6 py-12 font-sans">
      <div className="mb-12 flex justify-between items-end border-b border-border/40 pb-6">
        <div>
          <h1 className="text-4xl font-black tracking-tighter text-foreground mb-2 uppercase">
            Collection
          </h1>
          <p className="text-xs font-mono text-muted-foreground uppercase tracking-widest">
            Generated Assets Archive
          </p>
        </div>
        <Button variant="primary" onClick={createNew} className="rounded-full px-6 shadow-lg shadow-purple-500/20">
          <span className="uppercase text-xs font-bold tracking-wider">Create New</span>
        </Button>
      </div>

      {modelGroups.length === 0 ? (
        <div className="space-y-16">
          <div className="text-center py-32 bg-background/30 rounded-3xl border border-dashed border-border/50 backdrop-blur-sm">
            <div className="w-20 h-20 bg-background/50 rounded-full flex items-center justify-center mx-auto mb-6 border border-border/50">
                <Square2StackIcon className="w-8 h-8 text-muted-foreground" />
            </div>
            <p className="text-lg font-light text-muted-foreground mb-6 uppercase tracking-wide">No artifacts found</p>
            <Button variant="primary" onClick={createNew} className="rounded-full px-8 py-3">
              <span className="uppercase text-xs font-bold tracking-wider">Initialize First Creation</span>
            </Button>
          </div>
          
          <div>
            <h3 className="text-sm font-bold uppercase tracking-widest mb-8 text-muted-foreground">Curated Examples</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {CURATED_EXAMPLES.map((model) => (
                <ModelPreview key={model.id} model={model} />
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
          {modelGroups.map((group) => {
            const job = group.latest_job;
            const versionCount = group.version_count;
            
            return (
              <motion.div 
                key={group.group_id} 
                onClick={() => navigate({ to: `/app/ai-studio/model/${job.id}` })}
                initial="rest"
                whileHover="hover"
                animate="rest"
                className="group relative aspect-[4/5] rounded-2xl overflow-hidden bg-background-soft border border-border/50 cursor-pointer"
              >
                {/* Image/Preview */}
                <div className="absolute inset-0 bg-black/5 overflow-hidden">
                  <motion.img 
                    src={job.generated_image_urls?.[0] || job.generated_image_url} 
                    alt={job.name || job.prompt}
                    className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500"
                    variants={{
                        rest: { scale: 1.2 },
                        hover: { scale: 1.0 }
                    }}
                    transition={{ duration: 0.4, ease: "easeOut" }}
                  />
                </div>

                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent opacity-60 group-hover:opacity-90 transition-opacity duration-300" />

                {/* Content */}
                <div className="absolute inset-0 p-6 flex flex-col justify-between">
                  <div className="flex justify-between items-start">
                    <motion.span 
                        variants={{
                            rest: { y: -10, opacity: 0 },
                            hover: { y: 0, opacity: 1 }
                        }}
                        transition={{ duration: 0.3 }}
                        className={`px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider backdrop-blur-md shadow-lg ${
                            job.status === 'SUCCESS' ? 'bg-green-900/80 text-green-100 border border-green-500/50' : 
                            job.status === 'FAILED' ? 'bg-red-900/80 text-red-100 border border-red-500/50' : 
                            'bg-yellow-900/80 text-yellow-100 border border-yellow-500/50'
                        }`}
                    >
                      {job.status}
                    </motion.span>
                    {versionCount > 1 && (
                      <motion.span 
                        variants={{
                            rest: { x: 10, opacity: 0 },
                            hover: { x: 0, opacity: 1 }
                        }}
                        transition={{ duration: 0.3, delay: 0.1 }}
                        className="px-2 py-1 rounded bg-black/60 backdrop-blur-md text-white text-[10px] font-mono border border-white/20 shadow-lg"
                      >
                        v{versionCount}
                      </motion.span>
                    )}
                  </div>

                  <div>
                    <motion.div
                        variants={{
                            rest: { y: 10 },
                            hover: { y: 0 }
                        }}
                        transition={{ duration: 0.3 }}
                    >
                        <h3 className="text-white font-bold text-lg leading-tight mb-1 line-clamp-2">
                        {job.name || 'Untitled Asset'}
                        </h3>
                        <p className="text-white/60 text-xs font-mono line-clamp-1 mb-4">
                        {new Date(job.created_at).toLocaleDateString()}
                        </p>
                    </motion.div>
                    
                    <motion.div 
                        className="overflow-hidden"
                        variants={{
                            rest: { height: 0, opacity: 0 },
                            hover: { height: 'auto', opacity: 1 }
                        }}
                        transition={{ duration: 0.3 }}
                    >
                        <p className="text-white/80 text-xs line-clamp-2 mb-4">
                            {job.prompt}
                        </p>
                        <div className="flex items-center text-white/90 text-xs font-bold uppercase tracking-wider">
                            View Details <span className="ml-2">→</span>
                        </div>
                    </motion.div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
}